import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { getMaterials } from '../../../services/masterDataService';

const MaterialSelector = ({ selectedMaterials, onMaterialsChange }) => {
  const [masterMaterials, setMasterMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [quantities, setQuantities] = useState({});
  const [wastes, setWastes] = useState({});
  const prevSigRef = useRef(null);
  const dropdownRef = useRef(null);

  // Sync local quantities/wastes when selectedMaterials is replaced (e.g. loading a saved estimate)
  useEffect(() => {
    const currentSig = (selectedMaterials || [])
      ?.map(m => `${m?.masterId}:${m?.quantity ?? 1}:${m?.wastePercentage ?? 0}`)
      ?.sort()
      ?.join('|');
    if (prevSigRef?.current !== null && prevSigRef?.current !== currentSig) {
      const newQuantities = {};
      const newWastes = {};
      (selectedMaterials || [])?.forEach(m => {
        if (m?.masterId) {
          newQuantities[m.masterId] = m?.quantity ?? 1;
          newWastes[m.masterId] = m?.wastePercentage ?? 0;
        }
      });
      setQuantities(newQuantities);
      setWastes(newWastes);
    }
    prevSigRef.current = currentSig;
  }, [selectedMaterials]);

  useEffect(() => {
    const fetchMaterials = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getMaterials();
        setMasterMaterials(data || []);
      } catch (err) {
        console.error('Failed to load materials:', err);
        setError('Failed to load materials from master data.');
      } finally {
        setLoading(false);
      }
    };
    fetchMaterials();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(e?.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredMaterials = masterMaterials?.filter(m =>
    m?.itemName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    m?.itemCode?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    m?.category?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const isSelected = (id) => selectedMaterials?.some(m => m?.masterId === id);

  const handleToggle = (material) => {
    if (isSelected(material?.id)) {
      onMaterialsChange(selectedMaterials?.filter(m => m?.masterId !== material?.id));
    } else {
      const qty = parseFloat(quantities?.[material?.id]) || 1;
      const waste = parseFloat(wastes?.[material?.id] ?? material?.wastePercentage);
      // unitCost is always OMR (the DB stores OMR in unit_cost)
      const unitCostOmr = material?.unitCostOmr ?? material?.unitCost;
      const entry = {
        id: Date.now() + Math.random(),
        masterId: material?.id,
        item: material?.itemCode,
        itemLabel: material?.itemName,
        quantity: qty,
        unitCost: unitCostOmr,
        unitCostOmr,
        unitCostOriginal: material?.unitCostOriginal ?? material?.unitCost,
        currency: material?.currency || 'OMR',
        exchangeRate: material?.exchangeRate ?? 1,
        exchangeRateDate: material?.exchangeRateDate || new Date()?.toISOString()?.split('T')?.[0],
        uom: material?.uom,
        wastePercentage: waste,
      };
      onMaterialsChange([...selectedMaterials, entry]);
    }
  };

  const handleQuantityChange = (materialId, val) => {
    setQuantities(prev => ({ ...prev, [materialId]: val }));
    const idx = selectedMaterials?.findIndex(m => m?.masterId === materialId);
    if (idx >= 0) {
      const updated = [...selectedMaterials];
      updated[idx] = { ...updated?.[idx], quantity: parseFloat(val) || 1 };
      onMaterialsChange(updated);
    }
  };

  const handleWasteChange = (materialId, val) => {
    setWastes(prev => ({ ...prev, [materialId]: val }));
    const idx = selectedMaterials?.findIndex(m => m?.masterId === materialId);
    if (idx >= 0) {
      const updated = [...selectedMaterials];
      updated[idx] = { ...updated?.[idx], wastePercentage: parseFloat(val) || 0 };
      onMaterialsChange(updated);
    }
  };

  const handleRemoveMaterial = (id) => {
    onMaterialsChange(selectedMaterials?.filter(m => m?.id !== id));
  };

  const calculateMaterialCost = (material) => {
    const unitCostOmr = material?.unitCostOmr ?? material?.unitCost ?? 0;
    const baseCost = (material?.quantity || 0) * unitCostOmr;
    const wasteCost = baseCost * ((material?.wastePercentage || 0) / 100);
    return baseCost + wasteCost;
  };

  const selectedCount = selectedMaterials?.length || 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-foreground">Required Materials</label>
        {error && (
          <span className="text-xs text-error flex items-center gap-1">
            <Icon name="AlertCircle" size={12} />
            {error}
          </span>
        )}
      </div>
      {/* Searchable Dropdown Trigger */}
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setDropdownOpen(prev => !prev)}
          disabled={loading}
          className="w-full flex items-center justify-between px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground hover:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all disabled:opacity-60"
        >
          <span className={selectedCount === 0 ? 'text-muted-foreground' : ''}>
            {loading ? 'Loading materials...' : selectedCount === 0 ? 'Select materials...' : `${selectedCount} material${selectedCount > 1 ? 's' : ''} selected`}
          </span>
          {loading
            ? <Icon name="Loader2" size={16} className="text-muted-foreground animate-spin" />
            : <Icon name={dropdownOpen ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-muted-foreground" />
          }
        </button>

        {dropdownOpen && (
          <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg overflow-hidden">
            {/* Search */}
            <div className="p-2 border-b border-border">
              <div className="relative">
                <Icon name="Search" size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search materials..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e?.target?.value)}
                  className="w-full pl-8 pr-3 py-1.5 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-primary/50 text-foreground placeholder:text-muted-foreground"
                  autoFocus
                />
              </div>
            </div>

            {/* Items List */}
            <div className="max-h-52 overflow-y-auto">
              {filteredMaterials?.length === 0 && (
                <div className="px-3 py-3 text-sm text-muted-foreground text-center">
                  {masterMaterials?.length === 0 ? 'No materials in master data. Add them in Master Data Management.' : 'No materials found'}
                </div>
              )}
              {filteredMaterials?.map(material => {
                const checked = isSelected(material?.id);
                const currency = material?.currency || 'OMR';
                const isNonOMR = currency !== 'OMR';
                const unitCostOmr = material?.unitCostOmr ?? material?.unitCost;
                const unitCostOriginal = material?.unitCostOriginal ?? material?.unitCost;
                return (
                  <div key={material?.id} className="px-3 py-2 hover:bg-muted/50 transition-colors">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id={`mat-cb-${material?.id}`}
                        checked={checked}
                        onChange={() => handleToggle(material)}
                        className="w-4 h-4 rounded border-border text-primary accent-primary cursor-pointer"
                      />
                      <label htmlFor={`mat-cb-${material?.id}`} className="flex-1 cursor-pointer">
                        <div className="text-sm font-medium text-foreground">{material?.itemName}</div>
                        <div className="text-xs text-muted-foreground">
                          {material?.itemCode} · {material?.uom}
                          {isNonOMR ? (
                            <>
                              {' · '}<span className="text-amber-600 dark:text-amber-400 font-medium">{currency} {parseFloat(unitCostOriginal || 0)?.toFixed(3)}</span>
                              {' = '}<span className="text-primary font-medium">OMR {parseFloat(unitCostOmr || 0)?.toFixed(3)}</span>
                              {material?.exchangeRateDate && (
                                <span className="text-muted-foreground"> (rate: {parseFloat(material?.exchangeRate || 1)?.toFixed(4)}, {material?.exchangeRateDate})</span>
                              )}
                            </>
                          ) : (
                            <> · OMR {parseFloat(unitCostOmr || 0)?.toFixed(3)}</>
                          )}
                        </div>
                      </label>
                    </div>
                    {checked && (
                      <div className="mt-2 ml-6 flex gap-2">
                        <div className="flex-1">
                          <label className="text-xs text-muted-foreground mb-0.5 block">Qty</label>
                          <input
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={quantities?.[material?.id] ?? selectedMaterials?.find(m => m?.masterId === material?.id)?.quantity ?? 1}
                            onChange={e => handleQuantityChange(material?.id, e?.target?.value)}
                            className="w-full px-2 py-1 text-xs border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="text-xs text-muted-foreground mb-0.5 block">Waste %</label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.1"
                            value={wastes?.[material?.id] ?? selectedMaterials?.find(m => m?.masterId === material?.id)?.wastePercentage ?? material?.wastePercentage}
                            onChange={e => handleWasteChange(material?.id, e?.target?.value)}
                            className="w-full px-2 py-1 text-xs border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Info footer */}
            <div className="border-t border-border px-3 py-2 bg-muted/20">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Icon name="Info" size={11} />
                Manage materials in <span className="font-medium text-primary">Master Data Management</span>
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Selected Items List */}
      {selectedMaterials?.length > 0 ? (
        <div className="space-y-2">
          {selectedMaterials?.map((material) => {
            const currency = material?.currency || 'OMR';
            const isNonOMR = currency !== 'OMR';
            const totalCostOmr = calculateMaterialCost(material);
            return (
              <div
                key={material?.id}
                className="flex items-start justify-between gap-2 p-3 bg-card border border-border rounded-lg"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-foreground truncate">{material?.itemLabel}</div>
                  <div className="text-xs text-muted-foreground mt-1 break-words">
                    {material?.quantity} {material?.uom} ×{' '}
                    {isNonOMR ? (
                      <>
                        <span className="text-amber-600 dark:text-amber-400">{currency} {parseFloat(material?.unitCostOriginal || 0)?.toFixed(3)}</span>
                        {' (OMR '}{parseFloat((material?.unitCostOmr ?? material?.unitCost) || 0)?.toFixed(3)}{')'}
                      </>
                    ) : (
                      <>OMR {parseFloat(material?.unitCost || 0)?.toFixed(3)}</>
                    )}
                    {' + '}{material?.wastePercentage}% waste
                    {isNonOMR && material?.exchangeRateDate && (
                      <span className="ml-1 text-muted-foreground/70">· Rate locked {material?.exchangeRateDate}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="text-right">
                    <div className="text-sm font-semibold text-foreground whitespace-nowrap">
                      OMR {totalCostOmr?.toFixed(3)}
                    </div>
                    {isNonOMR && (
                      <div className="text-xs text-amber-600 dark:text-amber-400 whitespace-nowrap">
                        {currency} {(material?.quantity * (material?.unitCostOriginal ?? material?.unitCost) * (1 + (material?.wastePercentage || 0) / 100))?.toFixed(3)}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => handleRemoveMaterial(material?.id)}
                    className="text-error hover:text-error/80 transition-smooth"
                    aria-label="Remove material"
                  >
                    <Icon name="Trash2" size={18} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-6 text-muted-foreground text-sm">
          No materials selected yet. Use the dropdown above to select materials.
        </div>
      )}
    </div>
  );
};

export default MaterialSelector;